import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Building2, Save, X, MessageSquare } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';

export default function CompanyRemarksModal({ open, onClose, company, onRemarkUpdated }) {
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remarkData, setRemarkData] = useState(null);
  const { toast } = useToast();

  const maxLength = 10000;

  useEffect(() => {
    if (open && company) {
      fetchRemark();
    }
  }, [open, company]);

  const fetchRemark = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCompanyRemark(company._id);
      const data = response.data.data;
      setRemarkData(data);
      setRemark(data?.remark || '');
    } catch (error) {
      console.error('Error fetching remark:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch company remark',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateCompanyRemark(company._id, { remark });
      toast({
        title: 'Success',
        description: 'Company remark updated successfully',
      });

      // Notify parent to update hasRemark status
      // A remark exists if the trimmed text has content
      const hasRemark = remark.trim().length > 0;
      if (onRemarkUpdated) {
        onRemarkUpdated(company._id, hasRemark);
      }

      onClose();
    } catch (error) {
      console.error('Error saving remark:', error);
      toast({
        title: 'Error',
        description: 'Failed to save company remark',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setRemark('');
    setRemarkData(null);
    onClose();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Company Remarks
          </DialogTitle>
          <DialogDescription>
            Add or update internal notes for {company.companyName}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Company Info */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Building2 className="h-4 w-4 text-gray-500" />
              <div>
                <p className="font-medium text-sm">{company.companyName}</p>
                <p className="text-xs text-gray-500">{company.pocEmail}</p>
              </div>
            </div>

            {/* Remark Input */}
            <div className="space-y-2">
              <Label htmlFor="remark">Remarks</Label>
              <Textarea
                id="remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Enter internal notes or remarks about this company..."
                className="min-h-[200px] resize-none"
                maxLength={maxLength}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Internal notes visible only to admins</span>
                <span>{remark.length} / {maxLength}</span>
              </div>
            </div>

            {/* Metadata */}
            {remarkData && (
              <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-xs text-gray-600">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Created by:</span> {remarkData.createdBy}
                  </div>
                  <div>
                    <span className="font-medium">Created at:</span> {formatDate(remarkData.createdAt)}
                  </div>
                  <div>
                    <span className="font-medium">Updated by:</span> {remarkData.updatedBy}
                  </div>
                  <div>
                    <span className="font-medium">Updated at:</span> {formatDate(remarkData.updatedAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
